/**
 * Jubly SalesDrive — CRM Contacts Page
 * List view + detail view with timeline, deals, and tasks tabs
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  User, Users, Mail, Phone, Building2, ArrowLeft, Plus,
  Search, Edit2, Briefcase, CheckSquare, FileText, Check,
  MessageSquare, ArrowRight, Trophy, XCircle, Clock,
  ChevronDown, X, Loader2
} from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatCurrency(cents) {
  if (!cents && cents !== 0) return '$0';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(cents / 100);
}

const LIFECYCLE_COLORS = {
  subscriber:  'bg-gray-100 text-gray-600',
  lead:        'bg-blue-100 text-blue-700',
  mql:         'bg-indigo-100 text-indigo-700',
  sql:         'bg-violet-100 text-violet-700',
  opportunity: 'bg-amber-100 text-amber-700',
  customer:    'bg-green-100 text-green-700',
  evangelist:  'bg-pink-100 text-pink-700',
};

const LEAD_STATUS_COLORS = {
  new:         'bg-sky-100 text-sky-700',
  open:        'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  unqualified: 'bg-gray-100 text-gray-500',
};

const ACTIVITY_ICONS = {
  contact_created:   { Icon: User,          bg: 'bg-blue-100',   color: 'text-blue-600' },
  deal_created:      { Icon: Briefcase,     bg: 'bg-indigo-100', color: 'text-indigo-600' },
  deal_stage_change: { Icon: ArrowRight,    bg: 'bg-purple-100', color: 'text-purple-600' },
  deal_won:          { Icon: Trophy,        bg: 'bg-green-100',  color: 'text-green-600' },
  deal_lost:         { Icon: XCircle,       bg: 'bg-red-100',    color: 'text-red-500' },
  task_created:      { Icon: CheckSquare,   bg: 'bg-amber-100',  color: 'text-amber-600' },
  task_completed:    { Icon: Check,         bg: 'bg-green-100',  color: 'text-green-600' },
  note:              { Icon: FileText,      bg: 'bg-gray-100',   color: 'text-gray-600' },
  email_sent:        { Icon: Mail,          bg: 'bg-sky-100',    color: 'text-sky-600' },
  email_received:    { Icon: Mail,          bg: 'bg-sky-100',    color: 'text-sky-500' },
  sms_sent:          { Icon: MessageSquare, bg: 'bg-teal-100',   color: 'text-teal-600' },
  sms_received:      { Icon: MessageSquare, bg: 'bg-teal-100',   color: 'text-teal-500' },
  call_made:         { Icon: Phone,         bg: 'bg-violet-100', color: 'text-violet-600' },
  call_received:     { Icon: Phone,         bg: 'bg-violet-100', color: 'text-violet-500' },
};

function ActivityIcon({ type }) {
  const cfg = ACTIVITY_ICONS[type] || { Icon: FileText, bg: 'bg-gray-100', color: 'text-gray-500' };
  return (
    <div className={`w-8 h-8 rounded-full ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
      <cfg.Icon className={`w-4 h-4 ${cfg.color}`} />
    </div>
  );
}

function Badge({ label, colorClass }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
      {label?.replace(/_/g, ' ')}
    </span>
  );
}

function AvatarCircle({ name, size = 'md' }) {
  const letter = (name || '?')[0].toUpperCase();
  const sizes = { sm: 'w-8 h-8 text-sm', md: 'w-10 h-10 text-base', lg: 'w-14 h-14 text-xl' };
  const colors = ['bg-indigo-500', 'bg-blue-500', 'bg-violet-500', 'bg-pink-500', 'bg-amber-500', 'bg-teal-500'];
  const color = colors[letter.charCodeAt(0) % colors.length];
  return (
    <div className={`${sizes[size]} ${color} rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0`}>
      {letter}
    </div>
  );
}

// ─── Company Autocomplete Input ───────────────────────────────────────────────
// Lets users type a company name and pick from existing companies,
// or leave it as free text (the API will resolve/create it).

function CompanyInput({ value, onChange }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!value || value.length < 1) { setSuggestions([]); setOpen(false); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/crm/companies?search=${encodeURIComponent(value)}&limit=8`, { credentials: 'include' });
        const data = await res.json();
        setSuggestions(data.companies || []);
        setOpen((data.companies || []).length > 0);
      } catch { setSuggestions([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className="relative">
      <input
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        value={value}
        onChange={e => { onChange(e.target.value); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Acme Corp"
        autoComplete="off"
      />
      {loading && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-gray-400" />
      )}
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map(c => (
            <li
              key={c.id}
              onMouseDown={() => { onChange(c.name); setOpen(false); }}
              className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-indigo-50 cursor-pointer"
            >
              <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <span className="font-medium text-gray-800">{c.name}</span>
              {c.domain && <span className="text-gray-400 text-xs ml-auto">{c.domain}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Contact Form Modal ───────────────────────────────────────────────────────

const EMPTY_FORM = {
  firstName: '', lastName: '', email: '', phone: '',
  company: '', lifecycleStage: 'lead', leadSource: '', notes: '',
};

function ContactModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">
            {initial?.id ? 'Edit Contact' : 'New Contact'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">First Name</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={form.firstName} onChange={e => set('firstName', e.target.value)} placeholder="Jane" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Last Name</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={form.lastName} onChange={e => set('lastName', e.target.value)} placeholder="Smith" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
            <input type="email" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@example.com" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
            <input type="tel" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+1 555 000 0000" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Company</label>
            <CompanyInput value={form.company} onChange={v => set('company', v)} />
            <p className="text-[10px] text-gray-400 mt-1">Type to search existing companies or enter a new name</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Lifecycle Stage</label>
              <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={form.lifecycleStage} onChange={e => set('lifecycleStage', e.target.value)}>
                {['subscriber','lead','mql','sql','opportunity','customer','evangelist'].map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Lead Source</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={form.leadSource} onChange={e => set('leadSource', e.target.value)} placeholder="Website, Referral…" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any notes…" />
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2">
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {initial?.id ? 'Save Changes' : 'Create Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Note Modal ───────────────────────────────────────────────────────────────

function NoteModal({ contactId, onClose, onSaved }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    await fetch('/api/crm/activities', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contactId, activityType: 'note', title: 'Note', description: text }),
    });
    setSaving(false);
    onSaved();
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Add Note</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <textarea rows={4} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            value={text} onChange={e => setText(e.target.value)} placeholder="Write a note…" autoFocus />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving || !text.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-60">
              {saving ? 'Saving…' : 'Save Note'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Helpers: resolve company name → companyId ────────────────────────────────
// Looks up an existing company by exact name (case-insensitive).
// If not found, creates a new one. Returns the companyId or null.

async function resolveCompanyId(companyName) {
  if (!companyName || !companyName.trim()) return null;
  const name = companyName.trim();

  // Search for existing company
  const searchRes = await fetch(
    `/api/crm/companies?search=${encodeURIComponent(name)}&limit=10`,
    { credentials: 'include' }
  );
  if (searchRes.ok) {
    const data = await searchRes.json();
    const match = (data.companies || []).find(
      c => c.name.toLowerCase() === name.toLowerCase()
    );
    if (match) return match.id;
  }

  // Create new company
  const createRes = await fetch('/api/crm/companies', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (createRes.ok) {
    const company = await createRes.json();
    return company.id;
  }

  return null;
}

// ─── List View ────────────────────────────────────────────────────────────────

function ContactList({ onSelect }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [lifecycle, setLifecycle] = useState('');
  const [leadStatus, setLeadStatus] = useState('');
  const [showModal, setShowModal] = useState(searchParams.get('new') === '1');

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: 1, limit: 50 });
    if (search) params.set('search', search);
    if (lifecycle) params.set('lifecycleStage', lifecycle);
    if (leadStatus) params.set('leadStatus', leadStatus);
    const res = await fetch(`/api/crm/contacts?${params}`, { credentials: 'include' });
    const data = await res.json();
    setContacts(data.contacts || []);
    setTotal(data.total || 0);
    setLoading(false);
  }, [search, lifecycle, leadStatus]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  async function handleCreate(form) {
    // Resolve company name → companyId (look up or create)
    const companyId = await resolveCompanyId(form.company);

    const res = await fetch('/api/crm/contacts', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        companyId: companyId,
        lifecycleStage: form.lifecycleStage,
        leadSource: form.leadSource,
        notes: form.notes,
      }),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to create');
    fetchContacts();
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-white">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Search contacts…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select
          className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
          value={lifecycle}
          onChange={e => setLifecycle(e.target.value)}
        >
          <option value="">All Stages</option>
          {['subscriber','lead','mql','sql','opportunity','customer','evangelist'].map(s => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
        <select
          className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
          value={leadStatus}
          onChange={e => setLeadStatus(e.target.value)}
        >
          <option value="">All Statuses</option>
          {['new','open','in_progress','unqualified'].map(s => (
            <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors ml-auto"
        >
          <Plus className="w-4 h-4" /> Add Contact
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-gray-400">
            <Users className="w-8 h-8 mb-2 stroke-1" />
            <p className="text-sm">No contacts found</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {contacts.map(c => (
              <li
                key={c.id}
                onClick={() => onSelect(c.id)}
                className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <AvatarCircle name={c.first_name || c.email} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {[c.first_name, c.last_name].filter(Boolean).join(' ') || '(No name)'}
                    </p>
                    {c.lifecycle_stage && (
                      <Badge label={c.lifecycle_stage} colorClass={LIFECYCLE_COLORS[c.lifecycle_stage] || 'bg-gray-100 text-gray-600'} />
                    )}
                    {c.lead_status && (
                      <Badge label={c.lead_status} colorClass={LEAD_STATUS_COLORS[c.lead_status] || 'bg-gray-100 text-gray-600'} />
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    {c.email && <span className="text-xs text-gray-500 truncate">{c.email}</span>}
                    {c.phone && <span className="text-xs text-gray-400">{c.phone}</span>}
                    {c.company_name && (
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Building2 className="w-3 h-3" />{c.company_name}
                      </span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showModal && (
        <ContactModal
          onClose={() => { setShowModal(false); setSearchParams({}); }}
          onSave={handleCreate}
        />
      )}
    </div>
  );
}

// ─── Detail View ──────────────────────────────────────────────────────────────

function ContactDetail({ contactId, onBack }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [contact, setContact] = useState(null);
  const [tab, _setTab] = useState(() => searchParams.get('tab') || 'timeline');
  const setTab = (t) => {
    _setTab(t);
    const params = new URLSearchParams(searchParams);
    params.set('tab', t);
    setSearchParams(params, { replace: true });
  };
  const [timeline, setTimeline] = useState([]);
  const [deals, setDeals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showNote, setShowNote] = useState(false);

  async function loadContact() {
    const res = await fetch(`/api/crm/contacts/${contactId}`, { credentials: 'include' });
    const data = await res.json();
    setContact(data);
    setLoading(false);
  }

  async function loadTab(t) {
    setTabLoading(true);
    if (t === 'timeline') {
      const res = await fetch(`/api/crm/contacts/${contactId}/timeline`, { credentials: 'include' });
      const data = await res.json();
      setTimeline(Array.isArray(data) ? data : data.activities || []);
    } else if (t === 'deals') {
      const res = await fetch(`/api/crm/deals?contactId=${contactId}`, { credentials: 'include' });
      const data = await res.json();
      setDeals(Array.isArray(data) ? data : data.deals || []);
    } else if (t === 'tasks') {
      const res = await fetch(`/api/crm/tasks?contactId=${contactId}`, { credentials: 'include' });
      const data = await res.json();
      setTasks(Array.isArray(data) ? data : data.tasks || []);
    }
    setTabLoading(false);
  }

  useEffect(() => { loadContact(); }, [contactId]);
  useEffect(() => { if (!loading) loadTab(tab); }, [tab, loading]);

  async function handleUpdate(form) {
    // Resolve company name → companyId (look up or create)
    const companyId = await resolveCompanyId(form.company);

    const res = await fetch(`/api/crm/contacts/${contactId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        companyId: companyId,
        lifecycleStage: form.lifecycleStage,
        leadSource: form.leadSource,
        notes: form.notes,
      }),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to update');
    loadContact();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400">
        <p className="text-sm">Contact not found</p>
        <button onClick={onBack} className="mt-3 text-sm text-indigo-600 hover:underline">← Back</button>
      </div>
    );
  }

  const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(' ') || '(No name)';

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-6 py-4">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Contacts
        </button>
        <div className="flex items-start gap-4">
          <AvatarCircle name={contact.first_name || contact.email} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg font-bold text-gray-900">{fullName}</h1>
              {contact.lifecycle_stage && (
                <Badge label={contact.lifecycle_stage} colorClass={LIFECYCLE_COLORS[contact.lifecycle_stage] || 'bg-gray-100 text-gray-600'} />
              )}
              {contact.lead_status && (
                <Badge label={contact.lead_status} colorClass={LEAD_STATUS_COLORS[contact.lead_status] || 'bg-gray-100 text-gray-600'} />
              )}
            </div>
            <div className="flex items-center gap-4 mt-1 flex-wrap">
              {contact.email && (
                <span className="flex items-center gap-1 text-sm text-gray-500">
                  <Mail className="w-3.5 h-3.5" />{contact.email}
                </span>
              )}
              {contact.phone && (
                <span className="flex items-center gap-1 text-sm text-gray-500">
                  <Phone className="w-3.5 h-3.5" />{contact.phone}
                </span>
              )}
              {contact.company_name && (
                <span className="flex items-center gap-1 text-sm text-gray-500">
                  <Building2 className="w-3.5 h-3.5" />{contact.company_name}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => setShowEdit(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <Edit2 className="w-3.5 h-3.5" /> Edit
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 px-6 py-2 bg-white border-b border-gray-100">
        {['timeline', 'deals', 'tasks'].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              tab === t ? 'bg-indigo-50 text-indigo-700' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
        {tab === 'timeline' && (
          <button
            onClick={() => setShowNote(true)}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg"
          >
            <Plus className="w-3.5 h-3.5" /> Add Note
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50/40">
        {tabLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-5 h-5 animate-spin text-indigo-400" />
          </div>
        ) : tab === 'timeline' ? (
          <div className="px-6 py-4">
            {timeline.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No activity yet</p>
            ) : (
              <ul className="space-y-3">
                {timeline.map(a => (
                  <li key={a.id} className="flex items-start gap-3 bg-white rounded-xl border border-gray-100 px-4 py-3">
                    <ActivityIcon type={a.activity_type} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800">{a.title || a.activity_type?.replace(/_/g, ' ')}</p>
                      {a.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{a.description}</p>}
                    </div>
                    <span className="text-[11px] text-gray-400 flex-shrink-0">{timeAgo(a.created_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : tab === 'deals' ? (
          <div className="px-6 py-4">
            {deals.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No deals linked</p>
            ) : (
              <ul className="space-y-3">
                {deals.map(d => (
                  <li key={d.id} className="bg-white rounded-xl border border-gray-100 px-4 py-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-gray-800">{d.title}</p>
                      <span className="text-sm font-bold text-indigo-600">{formatCurrency(d.value)}</span>
                    </div>
                    {d.stage_name && (
                      <span className="inline-flex items-center mt-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                        {d.stage_name}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : tab === 'tasks' ? (
          <div className="px-6 py-4">
            {tasks.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No tasks</p>
            ) : (
              <ul className="space-y-3">
                {tasks.map(t => (
                  <li key={t.id} className="bg-white rounded-xl border border-gray-100 px-4 py-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-gray-800">{t.title}</p>
                      <Badge
                        label={t.status}
                        colorClass={t.status === 'completed' ? 'bg-green-100 text-green-700' : t.status === 'cancelled' ? 'bg-gray-100 text-gray-500' : 'bg-amber-100 text-amber-700'}
                      />
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      {t.task_type && <Badge label={t.task_type} colorClass="bg-blue-50 text-blue-600" />}
                      {t.due_date && (
                        <span className="flex items-center gap-1 text-xs text-gray-400">
                          <Clock className="w-3 h-3" />
                          {new Date(t.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </div>

      {showEdit && (
        <ContactModal
          initial={{
            id: contact.id,
            firstName: contact.first_name || '',
            lastName: contact.last_name || '',
            email: contact.email || '',
            phone: contact.phone || '',
            company: contact.company_name || '',
            lifecycleStage: contact.lifecycle_stage || 'lead',
            leadSource: contact.lead_source || '',
            notes: contact.notes || '',
          }}
          onClose={() => setShowEdit(false)}
          onSave={handleUpdate}
        />
      )}

      {showNote && (
        <NoteModal
          contactId={contactId}
          onClose={() => setShowNote(false)}
          onSaved={() => loadTab('timeline')}
        />
      )}
    </div>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────

export default function SalesContacts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const contactId = searchParams.get('id');

  function handleSelect(id) {
    setSearchParams({ id });
  }

  function handleBack() {
    setSearchParams({});
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {contactId ? (
        <ContactDetail contactId={contactId} onBack={handleBack} />
      ) : (
        <ContactList onSelect={handleSelect} />
      )}
    </div>
  );
}
